import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  DestroyRef,
} from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChatStateModule, ChatActions, getChatRoomsByType } from 'libs/shared/data/stores/chat';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { TabsModule } from 'primeng/tabs';
import { tap } from 'rxjs';

import { ChatRoomDisplayComponent } from '@offconon/shared/features/chat/chat-room-display';
import { AdsMessageRenderComponent } from '@offconon/shared/ui/ads-message-render';
import { InfiniteScrollDirective } from '@offconon/shared/ui/infinite-scroller';
import { ThemeService } from '@offconon/shared/ui/theme-switch';

import { ChatChannelListStoreModule } from '../chat-channel-list.module';
import { ChatChannelListActions } from '../state/chat-channel-list.actions';
import { selectChatSettings } from '../state/chat-channel-list.selectors';
@Component({
  selector: 'offconon-chat-channel-list',
  imports: [
    CommonModule,
    FormsModule,
    DividerModule,
    TranslateModule,
    MenuModule,
    MatIcon,
    ChatStateModule,
    ButtonModule,
    TabsModule,
    ChatRoomDisplayComponent,
    InfiniteScrollDirective,
    AdsMessageRenderComponent,
    ChatChannelListStoreModule,
  ],
  templateUrl: './chat-channel-list.component.html',
  styles: `
    input {
      border: none;
      outline: none;
      background-color: var(--box-bg-color);
      transition: transform 0.5s ease;
      border-radius: 50px;
    }
  `,
})
export class ChatChannelListComponent implements OnInit, OnChanges {
  @Input() logged_user: any;
  @Input() selectedBusiness: any;
  @Input() parameters: any;
  @Input() platform = 'core';
  @Input({ required: true }) chatSettings: any;
  @Output() selectedModule = new EventEmitter<string>();
  @Output() selectedChatRoomId = new EventEmitter<number>();

  items: MenuItem[];
  pageSize = 10;
  currentPage = 1;
  lazyLoading = false;

  private store = inject(Store);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private themeService = inject(ThemeService);

  isMobileView = false;
  create_room_parameters: any = {};
  selected_view_type: any;
  show_search_bar = false;

  rooms = toSignal(
    this.store.select(getChatRoomsByType('direct')).pipe(
      tap(() => {
        this.lazyLoading = false;
      }),
    ),

    {
      initialValue: {
        rooms: [],
        pagination: { page: 1, pageSize: this.pageSize, total: 0, totalPages: 1 },
      },
    },
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.['logged_user']?.currentValue?.['id'] !==
        changes?.['logged_user']?.previousValue?.['id'] &&
      changes?.['logged_user']?.currentValue !== undefined
    ) {
      this.loadInitialData();
    } else if (
      changes?.['selectedBusiness']?.currentValue?.['business_id'] !==
        changes?.['selectedBusiness']?.previousValue?.['business_id'] &&
      changes?.['selectedBusiness']?.currentValue !== undefined
    ) {
      this.loadInitialData();
    }

    if (changes['parameters']) {
      this.selected_view_type = this.parameters?.view_type;
    }
  }

  ngOnInit() {
    this.themeService.mobileView.subscribe((isMobileView) => {
      this.isMobileView = isMobileView;

      if (this.isMobileView) {
        this.show_search_bar = false;
      }
    });

    this.selected_view_type = this.parameters?.view_type;

    this.create_room_parameters = {
      logged_user: this.logged_user,
      room_type: 'group',
      entity_type: 'user',
    };
    this.renderMenu();
    this.store
      .select(selectChatSettings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings) => {
        if (settings) {
          this.chatSettings = settings;
          this.renderMenu();
        }
      });
  }

  searchbar() {
    this.show_search_bar = !this.show_search_bar;
  }

  renderMenu() {
    this.items = [
      {
        label: this.translateService.instant('Options'),
        visible: true,
        items: [
          {
            label: this.translateService.instant('Incoming call sound'),
            icon: this.chatSettings?.incoming_call ? 'phone_callback' : 'phone_disabled',
            visible: true,
            command: () => {
              this.settings({ incoming_call: !this.chatSettings?.incoming_call });
            },
          },
          {
            label: this.translateService.instant('Message sounds'),
            icon: this.chatSettings?.sound ? 'volume_up' : 'volume_off',
            visible: true,
            command: () => {
              this.settings({ sound: !this.chatSettings?.sound });
            },
          },
        ],
      },
    ];
  }

  settings(settings: any) {
    this.store.dispatch(
      ChatChannelListActions.updateChatSettings({
        settings,
      }),
    );

    //update selected_chat_settings with new data
    //update user store
    //save this.store.dispatch(.,...

    // update menu this.renderMenu(this.selected_chat_settings)
  }

  openSubPage(url: any) {
    this.router.navigate([url]);
  }

  write(module = 'chatwrite') {
    this.selectedModule.emit(module);
  }

  selectedRoomId(roomId: number): void {
    this.selectedChatRoomId.emit(roomId);
  }

  private loadInitialData(): void {
    this.lazyLoading = true;
    this.store.dispatch(
      ChatActions.fetchRooms({
        pageSize: this.pageSize,
        searchJson: {
          room_type: 'direct',
        },
      }),
    );
  }

  loadMoreData(): void {
    if (this.lazyLoading) return;
    this.lazyLoading = true;
    this.currentPage++;

    this.store.dispatch(
      ChatActions.fetchRooms({
        page: this.currentPage,
        pageSize: this.pageSize,
        searchJson: {
          room_type: 'direct',
        },
      }),
    );
  }
}
